import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Search, Filter, FileText, ChevronRight, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CaseList = ({ agent, onNavigate, filterStatus, isAdmin = false }) => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCases = useCallback(async () => {
    try {
      let url = (isAdmin || !agent || agent.id === 'admin')
        ? `${API_URL}/api/compliance/cases`
        : `${API_URL}/api/compliance/cases?advisor_id=${agent.id}`;

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setCases(data);
      }
    } catch (err) {
      console.error('Failed to fetch cases:', err);
    } finally {
      setLoading(false);
    }
  }, [agent, isAdmin]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);


  const handleDelete = async (caseItem, e) => {
    e.stopPropagation();
    const confirmText = `Permanently delete file ${caseItem.case_number}?\n\nThis cannot be undone. All fact-find, needs analysis, quotes, recommendation, and signature records will be removed.\n\nA confirmation email will be sent to the advisor on file.`;
    if (!window.confirm(confirmText)) return;
    try {
      const advisorId = (isAdmin || agent?.id === 'admin') ? 'admin' : agent.id;
      const advisorName = (isAdmin || agent?.id === 'admin') ? 'Admin' : (agent?.name || 'Advisor');
      const r = await fetch(
        `${API_URL}/api/compliance/cases/${caseItem.id}?advisor_id=${advisorId}&advisor_name=${encodeURIComponent(advisorName)}`,
        { method: 'DELETE' }
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert(`Delete failed: ${err.detail || r.statusText}`);
        return;
      }
      setCases(prev => prev.filter(c => c.id !== caseItem.id));
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    }
  };


  const filteredCases = cases.filter(c =>
    c.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.case_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.carrier?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#003D5C]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#003D5C]">Insurance Files</h1>
          <p className="text-sm text-gray-500">{cases.length} total files</p>
        </div>
        <Button
          onClick={() => onNavigate('new-case')}
          className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C] w-full sm:w-auto"
        >
          + New File
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
          />
        </div>
      </div>

      {/* Cases Table */}
      <Card>
        <CardContent className="p-0">
          {filteredCases.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-gray-600">Case</th>
                  <th className="text-left p-4 font-medium text-gray-600">Client</th>
                  <th className="text-left p-4 font-medium text-gray-600">Product</th>
                  <th className="text-left p-4 font-medium text-gray-600">Coverage</th>
                  <th className="text-left p-4 font-medium text-gray-600">Updated</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredCases.map(caseItem => {
                  return (
                    <tr 
                      key={caseItem.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => onNavigate('case-detail', { caseId: caseItem.id })}
                    >
                      <td className="p-4">
                        <p className="font-medium text-[#003D5C]">{caseItem.case_number}</p>
                        <p className="text-sm text-gray-500">{caseItem.carrier}</p>
                      </td>
                      <td className="p-4">
                        <p className="font-medium">{caseItem.client_name}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm">{caseItem.product_type}</p>
                      </td>
                      <td className="p-4">
                        <p className="font-medium">${caseItem.coverage_amount?.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">${caseItem.premium?.toLocaleString()}/{caseItem.payment_frequency}</p>
                      </td>
                      <td className="p-4 text-sm text-gray-500">
                        {new Date(caseItem.updated_at).toLocaleDateString()}
                      </td>
                      <td className="p-4 text-right">
                        <button
                          type="button"
                          onClick={(e) => handleDelete(caseItem, e)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded mr-2"
                          title="Delete file"
                          data-testid={`delete-case-${caseItem.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <ChevronRight className="h-5 w-5 text-gray-400 inline" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No cases found</p>
              <Button 
                onClick={() => onNavigate('new-case')}
                className="mt-4 bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]"
              >
                Create Your First File
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CaseList;
